import { useEffect, useState } from 'react';

interface ServiceWorkerState {
    isUpdateAvailable: boolean;
    isOffline: boolean;
    registration: ServiceWorkerRegistration | null;
}

export function useServiceWorker() {
    const [state, setState] = useState<ServiceWorkerState>({
        isUpdateAvailable: false,
        isOffline: !navigator.onLine,
        registration: null,
    });

    useEffect(() => {
        // Verificar si el navegador soporta Service Workers
        if (!('serviceWorker' in navigator)) {
            console.log('Service Workers no soportados en este navegador');
            return;
        }

        let registration: ServiceWorkerRegistration | null = null;

        // Registrar el Service Worker
        const registerServiceWorker = async () => {
            try {
                registration = await navigator.serviceWorker.register('/sw.js', {
                    scope: '/',
                });

                console.log('[App] ✅ Service Worker registrado:', registration.scope);
                console.log('[App] 📍 SW URL:', registration.active?.scriptURL || registration.installing?.scriptURL);
                setState((prev) => ({ ...prev, registration }));

                // 🚀 OPTIMIZACIÓN PARA BOLT.NEW: Verificar actualizaciones cada 15 segundos
                // Bolt.new no permite configurar headers HTTP, así que necesitamos ser más agresivos
                setInterval(() => {
                    if (registration) {
                        console.log('[App] 🔄 Verificando actualizaciones del SW (Bolt.new mode)...');
                        registration.update().catch((err) => {
                            console.warn('[App] ⚠️ No se pudo verificar la actualización del Service Worker (offline o red inestable):', err);
                        });
                    }
                }, 15000); // 15 segundos para Bolt.new (antes era 60s)

                // Detectar cuando hay una actualización disponible
                registration.addEventListener('updatefound', () => {
                    const newWorker = registration?.installing;

                    if (!newWorker) return;

                    newWorker.addEventListener('statechange', () => {
                        console.log('[App] 🔄 SW state changed:', newWorker.state);
                        if (
                            newWorker.state === 'installed' &&
                            navigator.serviceWorker.controller
                        ) {
                            console.log('[App] 🎉 Nueva versión disponible!');
                            setState((prev) => ({ ...prev, isUpdateAvailable: true }));
                        }
                    });
                });

                // Verificar inmediatamente si hay actualizaciones
                await registration.update();
            } catch (error) {
                console.error('[App] Error al registrar Service Worker:', error);
            }
        };

        // Detectar cambios en el controlador (cuando se activa una nueva versión)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (!refreshing) {
                refreshing = true;
                console.log('[App] Nueva versión activada, recargando...');
                window.location.reload();
            }
        });

        // Detectar cambios en la conectividad
        const handleOnline = () => {
            console.log('[App] Conexión restaurada');
            setState((prev) => ({ ...prev, isOffline: false }));
            // Verificar actualizaciones al volver online
            if (registration) {
                registration.update().catch((err) => {
                    console.warn('[App] ⚠️ No se pudo actualizar el Service Worker al volver online:', err);
                });
            }
        };

        const handleOffline = () => {
            console.log('[App] Conexión perdida');
            setState((prev) => ({ ...prev, isOffline: true }));
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Iniciar registro
        registerServiceWorker();

        // Cleanup
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Función para aplicar la actualización manualmente
    const applyUpdate = () => {
        console.log('[App] 🚀 Aplicando actualización...');
        if (state.registration?.waiting) {
            console.log('[App] 📨 Enviando comando SKIP_WAITING al SW...');
            // Enviar mensaje al SW para que se active inmediatamente
            state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
            console.warn('[App] ⚠️ No hay SW en estado waiting');
        }
    };

    return {
        ...state,
        applyUpdate,
    };
}
